import type { ReactNode } from 'react';
import type { FlagRow, FlagsListManager } from '@/state/flags/useFlags';
import type { UseDataControlReturn } from '@/components/DataTable';
import { useEffect, useState } from 'react';
import { ExternalLink, Flag, Plus } from 'lucide-react';
import { formatDateTime } from 'helpers/formatters';
import { cn } from 'helpers/cn';
import { NavLink } from '@/components/NavLink';
import { DataTable, useDataControl } from '@/components/DataTable';
import { SearchInput } from '@/components/SearchInput';
import { Button } from '@/components/ui/button';
import { useFlagsList } from '@/state/flags/useFlags';
import { percentOf } from '@/state/flags/flag-status';
import { DisclosureButton, FOCUS_RING, Meter, SEARCH_FIELD_CLASS } from '../ConsolePanel';
import { FirstRunState } from '../FirstRunState';
import { CreateFlagDialog } from './CreateFlagDialog';
import { FlagKindChip, FlagStatusChip } from './FlagChips';
import { FLAG_COPY } from './copy';

const SEARCH_KEYS: (keyof FlagRow)[] = ['id', 'name', 'flagKey', 'statusLabel'];
const PAGE_SIZE = 10;
const COPY = FLAG_COPY.list;

/**
 * Feature flags, Library: the website's flags in the Library's layout, one toolbar over one
 * table, status views as pills. A flag's row says where its rollout is and what happens next.
 */
export const FlagsList = () => {
  const list = useFlagsList();
  const [isCreating, setIsCreating] = useState(false);
  const dataController = useDataControl<FlagRow>(list.rows);

  // Back to page one when the view narrows the set (the table only resets on its own search).
  useEffect(() => {
    dataController.changePage(1);
  }, [list.view]);

  const createDialog = isCreating ? <CreateFlagDialog list={list} onClose={() => setIsCreating(false)} /> : null;

  if (!list.hasWebsite) return <ListNotice>{COPY.selectWebsite}</ListNotice>;
  if (list.isLoading) return <ListNotice>{COPY.loading}</ListNotice>;
  if (list.isError) {
    return (
      <ListNotice>
        {COPY.loadFailed}
        <div>
          <DisclosureButton onClick={list.refetch}>{COPY.retry}</DisclosureButton>
        </div>
      </ListNotice>
    );
  }

  if (list.totalCount === 0) {
    return (
      <main className="flex flex-col gap-[14px]">
        <FirstRunState
          icon={Flag}
          title={FLAG_COPY.empty.title}
          description={FLAG_COPY.empty.description}
          action={
            list.canCreate ? (
              <Button onClick={() => setIsCreating(true)} className="h-9 rounded-lg">
                <Plus className="size-4" />
                {COPY.create}
              </Button>
            ) : undefined
          }
        />
        {createDialog}
      </main>
    );
  }

  return (
    <main className="flex flex-col gap-[14px]">
      <section className="bg-panel border-line rounded-panel border">
        <FlagsToolbar list={list} dataController={dataController} onCreate={() => setIsCreating(true)} />
        <div className="p-3">
          <DataTable<FlagRow>
            dataController={dataController}
            searchKeys={SEARCH_KEYS}
            pageSize={PAGE_SIZE}
            noDataMessage={COPY.emptyView}
            noMatchMessage={COPY.noMatch}
            noDataCTA={
              list.view !== 'All' ? (
                <div className="mt-2">
                  <DisclosureButton onClick={() => list.setView('All')}>{COPY.showAll}</DisclosureButton>
                </div>
              ) : undefined
            }
            columns={[
              {
                field: 'name',
                label: 'Flag',
                sortKey: 'name',
                headerClassName: 'micro-label',
                cellClassName: 'min-w-0',
                mobile: 'title',
                format: ({ item }) => (
                  <div className="flex min-w-0 flex-col gap-0.5">
                    <NavLink
                      to={`/flags/${item.id}`}
                      title={item.name}
                      className="text-foreground flex min-w-0 items-center gap-2 font-medium hover:underline"
                    >
                      <span className="truncate">{item.name}</span>
                      <ExternalLink className="text-dim h-3.5 w-3.5 flex-shrink-0" />
                    </NavLink>
                    <span className="text-dim truncate font-mono text-micro">{item.flagKey}</span>
                  </div>
                ),
              },
              {
                field: 'kind',
                label: 'Kind',
                sortKey: 'kind',
                headerClassName: 'micro-label w-24',
                mobile: 'meta',
                format: ({ item }) => <FlagKindChip kind={item.kind} />,
              },
              {
                field: 'statusOrder',
                label: 'Status',
                sortKey: 'statusOrder',
                headerClassName: 'micro-label w-36',
                mobile: 'meta',
                format: ({ item, idx }) => (
                  <div className="flex flex-col items-start gap-1" data-tour={idx === 0 ? 'flags-status' : undefined}>
                    <FlagStatusChip status={item.statusLabel} />
                    {item.progress && <span className="text-dim text-micro">{item.progress}</span>}
                  </div>
                ),
              },
              {
                field: 'rolloutBp',
                label: 'Rollout',
                sortKey: 'rolloutBp',
                headerClassName: 'micro-label w-24',
                cellClassName: 'num',
                mobile: 'meta',
                format: ({ item }) =>
                  item.status === 'Running' ? <RolloutCell bp={item.rolloutBp} /> : <span className="text-dim">-</span>,
              },
              {
                field: 'next',
                label: 'Next step',
                headerClassName: 'micro-label w-40',
                cellClassName: 'text-dim text-meta',
                mobile: 'meta',
                format: ({ item, idx }) => (
                  <span
                    data-tour={idx === 0 ? 'flags-next-step' : undefined}
                    className={cn(item.next === 'Waiting for you' && 'text-warn')}
                  >
                    {item.next ?? '-'}
                  </span>
                ),
              },
              {
                field: 'changedAt',
                label: 'Updated',
                sortKey: 'changedAt',
                headerClassName: 'micro-label w-36',
                cellClassName: 'num text-dim',
                mobile: 'hide',
                format: ({ value }) => formatDateTime(value as number) || '-',
              },
            ]}
          />
        </div>
      </section>
      {createDialog}
    </main>
  );
};

type RolloutCellProps = { bp: number };

/** The share as a number and a thin bar, so a column of them reads at a glance. */
const RolloutCell = ({ bp }: RolloutCellProps) => (
  <div className="flex min-w-[64px] flex-col gap-1">
    <span className="text-foreground">{percentOf(bp)}</span>
    <span className="block w-16" aria-hidden="true">
      <Meter percent={bp / 100} className="bg-ok" />
    </span>
  </div>
);

type FlagsToolbarProps = {
  list: FlagsListManager;
  dataController: UseDataControlReturn<FlagRow>;
  onCreate: () => void;
};

/** The Library's toolbar: view pills left, search and Create right, stacked below md. */
const FlagsToolbar = ({ list, dataController, onCreate }: FlagsToolbarProps) => {
  const reset = () => {
    dataController.setSearchQuery('');
    dataController.setSortedBy(null);
    list.setView('All');
  };

  return (
    <div className="border-line flex flex-col gap-2 border-b px-4 py-3 md:flex-row md:flex-wrap md:items-center">
      <div className="-mx-1 flex min-w-0 items-center gap-1.5 overflow-x-auto px-1 py-0.5" data-tour="flags-views">
        {list.views.map((view) => (
          <button
            key={view}
            type="button"
            onClick={() => list.setView(view)}
            className={cn(
              'micro-label rounded-pill flex min-h-9 shrink-0 items-center border px-3 py-1.5',
              'transition-colors duration-150 md:min-h-0',
              FOCUS_RING,
              view === list.view
                ? 'bg-sel-bg border-sel-line text-foreground'
                : 'border-input text-dim hover:bg-hover hover:text-foreground',
            )}
          >
            {`${view} (${list.counts[view]})`}
          </button>
        ))}
        <button
          type="button"
          onClick={reset}
          className={cn(
            'micro-label rounded-pill border-input text-dim hover:bg-hover hover:text-foreground',
            'flex min-h-9 shrink-0 items-center border px-3 py-1.5 transition-colors duration-150 md:min-h-0',
            FOCUS_RING,
          )}
        >
          {COPY.reset}
        </button>
      </div>

      <div className="flex flex-col gap-2 md:ml-auto md:shrink-0 md:flex-row md:items-center">
        <div className="relative w-full md:w-auto" data-tour="flags-search">
          <SearchInput
            value={dataController.searchQuery}
            handleSearch={dataController.setSearchQuery}
            placeholder={COPY.search}
            className={SEARCH_FIELD_CLASS}
          />
        </div>
        {list.canCreate && (
          <Button onClick={onCreate} className="h-9 w-full shrink-0 rounded-lg md:w-auto" data-tour="flags-new">
            <Plus className="mr-2 h-4 w-4" />
            {COPY.create}
          </Button>
        )}
      </div>
    </div>
  );
};

type ListNoticeProps = { children: ReactNode };

/** The one-line answer while the list cannot be drawn, in the panel the table would fill. */
const ListNotice = ({ children }: ListNoticeProps) => (
  <main className="bg-panel border-line rounded-panel border px-6 py-12">
    <div className="supporting flex flex-col items-center gap-2 text-center">{children}</div>
  </main>
);
